/**
 * Weight, expenditure and intake over time. Three charts, one axis each —
 * weight and calories never share a chart.
 */

import { useState } from 'react'

import { useModel } from '../db/queries'
import { useApp } from '../context'
import { BarChart } from '../components/charts/BarChart'
import { LineChart } from '../components/charts/LineChart'
import { addDays, dayRange, daysBetween } from '../lib/days'
import { fmtKcal } from '../lib/nutrients'
import { formatRate, formatWeight, kgToDisplay } from '../lib/units'

const RANGES = [['1M', 30], ['3M', 91], ['6M', 182], ['1Y', 365], ['All', 0]] as const

export function TrendsScreen() {
  const { unit } = useApp()
  const m = useModel()
  const [range, setRange] = useState<number>(30)
  if (!m) return <p className="muted">Loading…</p>

  const today = m.today
  const earliest = [m.trend[0]?.day, [...m.totals.keys()].sort()[0]].filter(Boolean).sort()[0] ?? today
  const from = range ? addDays(today, -(range - 1)) : earliest
  const inRange = (d: string) => d >= from && d <= today

  const trend = m.trend.filter((p) => inRange(p.day))
  const exp = m.expenditure.filter((p) => inRange(p.day))
  const days = dayRange(from < earliest && !range ? earliest : from, today)
  const bars = days.map((d) => {
    const s = m.summaries.get(d)
    const t = m.targetOn(d)
    return { day: d, value: s?.kcal ?? 0, muted: s ? !s.valid : true, target: t?.kcal ?? null }
  }).filter((b, i, all) => b.value > 0 || all.slice(0, i).some((x) => x.value > 0))

  // 7-day average of valid intake, for the expenditure chart.
  const avgIntake = days.map((d) => {
    let s = 0, n = 0
    for (let i = 0; i < 7; i++) {
      const x = m.summaries.get(addDays(d, -i))
      if (x?.valid) { s += x.kcal; n++ }
    }
    return n >= 3 ? { day: d, y: s / n } : null
  }).filter((x): x is { day: string; y: number } => x !== null)

  const wChange = trend.length >= 2 ? trend[trend.length - 1].trend - trend[0].trend : null
  const weeks = trend.length >= 2 ? daysBetween(trend[0].day, trend[trend.length - 1].day) / 7 : 0
  const valid = bars.filter((b) => !b.muted)
  const avgKcal = valid.length ? valid.reduce((a, b) => a + b.value, 0) / valid.length : null

  return (
    <div>
      <h1>Trends</h1>
      <div className="pill-row">
        {RANGES.map(([l, n]) => <button key={l} className={`pill ${range === n ? 'on' : ''}`} onClick={() => setRange(n)}>{l}</button>)}
      </div>

      <div className="grid3 small" style={{ marginTop: '1rem' }}>
        <div className="card" style={{ padding: '.7rem' }}><div className="muted">Weight change</div>
          <div className="num" style={{ fontWeight: 600 }}>{wChange !== null ? formatWeight(wChange, unit).replace(/^(?!-)/, wChange > 0 ? '+' : '') : '—'}</div></div>
        <div className="card" style={{ padding: '.7rem' }}><div className="muted">Avg rate</div>
          <div className="num" style={{ fontWeight: 600 }}>{wChange !== null && weeks >= 1 ? formatRate(wChange / weeks, unit) : '—'}</div></div>
        <div className="card" style={{ padding: '.7rem' }}><div className="muted">Avg intake</div>
          <div className="num" style={{ fontWeight: 600 }}>{avgKcal ? `${fmtKcal(avgKcal)}` : '—'}</div></div>
      </div>

      <h2>Weight</h2>
      <div className="card">
        <LineChart format={(v) => (kgToDisplay(v, unit) ?? 0).toFixed(1)} empty="Two weigh-ins and the trend appears here."
          series={[
            { id: 'scale', label: 'Scale weight', color: 'var(--scale-dot)', kind: 'dots', points: trend.map((p) => ({ day: p.day, y: p.kg })) },
            { id: 'trend', label: 'Trend', color: 'var(--protein)', points: trend.map((p) => ({ day: p.day, y: p.trend })) },
          ]} />
        <p className="muted small" style={{ marginBottom: 0 }}>The trend smooths out water and salt swings. Watch it, not the dots.</p>
      </div>

      <h2>Expenditure</h2>
      <div className="card">
        <LineChart format={(v) => fmtKcal(v)} empty="Set up your plan to see expenditure."
          series={[
            { id: 'E', label: 'Expenditure', color: 'var(--carbs)', points: exp.map((p) => ({ day: p.day, y: p.E })) },
            { id: 'I', label: 'Intake, 7-day avg', color: 'var(--fat)', dashed: true, points: avgIntake },
          ]} />
        <p className="muted small" style={{ marginBottom: 0 }}>When the dashed intake line sits below expenditure, you're in a deficit.</p>
      </div>

      <h2>Calories</h2>
      <div className="card">
        <BarChart bars={bars} label="Daily calories against target" format={(v) => fmtKcal(v)} />
        <p className="muted small" style={{ marginBottom: 0 }}>Dashed line: your target at the time. Hollow bars aren't counted (incomplete).</p>
      </div>
    </div>
  )
}
